import _isEmpty from 'lodash/isEmpty';
import { REVIEW_ACTION_TYPE } from '../../../../../common/review-action/constants';
import type { GenericAnnotationReviewDataT } from '../../../../../common/types/types';
import { type AnnotationLabelT } from '../../../../../common/types/types';
import { getRandomId, initializeAnnotationReviewData } from '../../../../../common/utils/utils';
import type { AtomSetter } from '../../../../labelling-v2/types/setter-types';
import { ANNOTATION_ML_STATE, ANNOTATION_TYPE } from '../../../constants';
import type { AnnotationReviewStoreT, AnnotationStoreT, AnnotationT, FrameStateT, LabelStateT } from '../../../types';
import { type AnnotationDataT, type BrushAnnotationT } from '../../../types';
import { type AddAnnotationActionT, type DeleteAnnotationActionT, type UpdateAnnotationActionT } from '../../actions';
import { interpolateKeyFrames, interpolateShapes } from '../../utils/interpolation';
import { processAnnotationFrameSpans, removeAnnotationFrameSpans, showHideAnnotationFromAnnotationStore } from '../../utils/utils';
import { boundCoordinate } from './utils/bound-coordinate';
import type { BulkAddAnnotationsPayloadT, BulkUpdateAnnotationsPayloadT, DeleteAnnotationPayloadT, DeleteAnnotationsPayloadT } from './annotations';
import { type ImportAnnotationsPayloadT } from './annotations';
export const getAnnotationData = (state: LabelStateT, frameIndex: number, annotationIdx: string): AnnotationT => {
  const frames: Array<FrameStateT> = [...state.frames];
  // @ts-expect-error todo(ts-migration) TS2322 Type 'AnnotationT | undefined' is not assignable to type 'AnnotationT'.
  return frames[frameIndex].annotationStore[annotationIdx];
};
export const getAnnotationReviewData = (state: LabelStateT, annotationIdx: string): GenericAnnotationReviewDataT => {
  // @ts-expect-error todo(ts-migration) TS2322 Type 'GenericAnnotationReviewDataT | undefined' is not assignable to type 'GenericAnnotationReviewDataT'.
  return state.annotationReviewStore[annotationIdx];
};
const getSelectedAnnotationIDs = (state: LabelStateT, payload: Omit<AddAnnotationActionT, 'type'>) => {
  return !_isEmpty(state.selectedAnnotationLabel?.questionnaire) ? [payload.annotationIdx] : state.selectedAnnotationIds;
};
export const addAnnotation = (state: LabelStateT, payload: Omit<AddAnnotationActionT, 'type'>, stateOverrides: {
  shouldSetSelectedAnnotationIds?: boolean;
  shouldShowAnnotationDetails?: boolean;
} = {}, atomSetter?: AtomSetter) => {
  const frames = [...state.frames];

  // @ts-expect-error todo(ts-migration) TS2322 Type '{ annotationStore: { [x: string]: AnnotationT; }; imageUrl?: string | undefined; annotationMLStore?: AnnotationStoreT; annotationConsensusStore?: AnnotationStoreT; }' is not assignable to type '...
  frames[payload.frameIndex] = {
    ...frames[payload.frameIndex],
    annotationStore: {
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      ...frames[payload.frameIndex].annotationStore,
      [payload.annotationIdx]: payload.annotationData
    }
  };
  const annotationReviewStore = {
    ...state.annotationReviewStore,
    ...(payload.annotationReviewData && {
      [payload.annotationIdx]: payload.annotationReviewData
    })
  };

  // Set defaults for stateOverrides param
  const {
    shouldSetSelectedAnnotationIds = true,
    shouldShowAnnotationDetails = true
  } = stateOverrides;
  atomSetter?.setAnnotationFrames(frames);
  atomSetter?.setSelectedAnnotationIds(shouldSetSelectedAnnotationIds ? getSelectedAnnotationIDs(state, payload) : []);
  atomSetter?.setShowAnnotationDetails(shouldShowAnnotationDetails ? !_isEmpty(payload.annotationData.annotationLabel.questionnaire) : false);
  atomSetter?.setCopiedAnnotationId(null);
  return {
    ...state,
    frames,
    annotationReviewStore,
    // If the new annotation contains questionnaire, setting it as selected and tool as pointer.
    // TODO: Find a way to decouple questionnaire from label store provider
    selectedAnnotationIds: shouldSetSelectedAnnotationIds ? getSelectedAnnotationIDs(state, payload) : [],
    showAnnotationDetails: shouldShowAnnotationDetails ? !_isEmpty(payload.annotationData.annotationLabel.questionnaire) : false,
    copiedAnnotationId: null
  };
};
export const bulkAddAnnotations = (state: LabelStateT, payload: BulkAddAnnotationsPayloadT, atomSetter?: AtomSetter) => {
  const frames = [...state.frames];
  const updatedFrames = frames.map((frame, frameIndex) => {
    const frameAnnotations = payload.annotations.find(annotation => annotation.frameIndex === frameIndex);
    if (!frameAnnotations) {
      return frame;
    }
    const updatedAnnotationStore = {
      ...frame.annotationStore,
      ...Object.fromEntries(frameAnnotations.annotations.map(({
        id,
        annotation
      }) => [id, {
        ...annotation,
        metadata: {
          ...annotation.metadata,
          annotationSource: undefined
        }
      }]))
    };
    return {
      ...frame,
      annotationStore: updatedAnnotationStore
    };
  });
  atomSetter?.setAnnotationFrames(updatedFrames);
  return {
    ...state,
    frames: updatedFrames
  };
};
export const bulkDeleteAnnotations = (state: LabelStateT, payload: BulkAddAnnotationsPayloadT, atomSetter?: AtomSetter) => {
  const frames = [...state.frames];
  const updatedFrames = frames.map((frame, frameIndex) => {
    const frameAnnotations = payload.annotations.find(annotation => annotation.frameIndex === frameIndex);
    if (!frameAnnotations) {
      return frame;
    }
    const updatedAnnotationStore = {
      ...frame.annotationStore
    };
    frameAnnotations.annotations.forEach(annotation => {
      delete updatedAnnotationStore[annotation.id];
    });
    return {
      ...frame,
      annotationStore: updatedAnnotationStore
    };
  });
  atomSetter?.setAnnotationFrames(updatedFrames);
  return {
    ...state,
    frames: updatedFrames
  };
};
export const bulkRemoveAnnotations = (state: LabelStateT, payload: BulkUpdateAnnotationsPayloadT, atomSetter?: AtomSetter) => {
  const frames = [...state.frames];
  const getInterpolationKeyFrameStatus = (isStartFrame: boolean) => isStartFrame;
  const updatedFrames = removeAnnotationFrameSpans(frames, payload.annotationIdx, {
    start: payload.startFrame,
    end: payload.endFrame
  }, getInterpolationKeyFrameStatus);
  atomSetter?.setAnnotationFrames(updatedFrames);
  return {
    ...state,
    frames: updatedFrames
  };
};
export const bulkUpdateAnnotation = (state: LabelStateT, payload: BulkUpdateAnnotationsPayloadT, atomSetter?: AtomSetter) => {
  const {
    annotationIdx,
    startFrame,
    endFrame
  } = payload;

  // For interpolation, only start frames are keyframes
  const getInterpolationKeyFrameStatus = (isStartFrame: boolean) => isStartFrame;
  const frames = processAnnotationFrameSpans(state.frames, annotationIdx, {
    start: startFrame,
    end: endFrame
  }, getInterpolationKeyFrameStatus);
  atomSetter?.setAnnotationFrames(frames);
  return {
    ...state,
    frames
  };
};
export const restoreAnnotations = (state: LabelStateT, annotationStoresToBeRestored: AnnotationStoreT[], annotationReviewDataToBeRestored: GenericAnnotationReviewDataT[], parentAnnotationsToBeRestored: AnnotationStoreT[] | undefined, atomSetter?: AtomSetter) => {
  const frames = [...state.frames];
  annotationStoresToBeRestored.forEach((annotationStore, index) =>
  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  frames[index].annotationStore = {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    ...frames[index].annotationStore,
    ...annotationStore,
    ...parentAnnotationsToBeRestored?.[index]
  });
  const annotationReviewStore = annotationReviewDataToBeRestored.reduce((annotationReviewStore, annotation) => {
    return {
      ...annotationReviewStore,
      [annotation.annotationId]: annotation
    };
  }, {});
  atomSetter?.setAnnotationFrames(frames);
  return {
    ...state,
    frames,
    annotationReviewStore
  };
};
export const updateAnnotation = (state: LabelStateT, payload: Omit<UpdateAnnotationActionT, 'type'>, atomSetter?: AtomSetter) => {
  let frames = [...state.frames];
  const {
    frameIndex,
    annotationIdx,
    annotationData
  } = payload;
  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  const existingAnnotation = frames[frameIndex].annotationStore[annotationIdx];

  // @ts-expect-error todo(ts-migration) TS2322 Type '{ annotationStore: { [x: string]: AnnotationT; }; imageUrl?: string | undefined; annotationMLStore?: AnnotationStoreT; annotationConsensusStore?: AnnotationStoreT; }' is not assignable to type '...
  frames[frameIndex] = {
    ...frames[frameIndex],
    annotationStore: {
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      ...frames[frameIndex].annotationStore,
      [annotationIdx]: annotationData
    }
  };

  // Annotation title is updated, need to update it in all frames
  // @ts-expect-error todo(ts-migration) TS18048 'existingAnnotation' is possibly 'undefined'.
  if (existingAnnotation.annotationLabel.title !== annotationData.annotationLabel.title) {
    frames = frames.map(frame => {
      if (!frame.annotationStore[annotationIdx]) {
        return frame;
      }
      frame.annotationStore[annotationIdx].annotationLabel = annotationData.annotationLabel;
      return frame;
    });
  }

  // Interpolate for video mode
  if (frames.length > 1) {
    frames = interpolateKeyFrames<(typeof ANNOTATION_TYPE)[keyof typeof ANNOTATION_TYPE], AnnotationDataT, FrameStateT>(frames, frameIndex, annotationIdx, interpolateShapes);
  }
  atomSetter?.setAnnotationFrames(frames);
  return {
    ...state,
    frames
  };
};
export const deletedAnnotation = (state: LabelStateT, payload: Omit<DeleteAnnotationActionT, 'type'>, atomSetter?: AtomSetter) => {
  const frames: FrameStateT[] = [...state.frames];
  const {
    annotationIdx,
    frameIndex
  } = payload;
  const {
    [annotationIdx]: _deletedAnnotation,
    ...updatedAnnotationStore
  } =
  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  frames[frameIndex].annotationStore;

  //TODO: remove this action and use pubSub model
  const {
    [annotationIdx]: _deletedAnnotationReviewData,
    ...updatedAnnotationReviewStore
  } = state.annotationReviewStore;

  // @ts-expect-error todo(ts-migration) TS2322 Type '{ annotationStore: { [key: string]: AnnotationT; }; imageUrl?: string | undefined; annotationMLStore?: AnnotationStoreT; annotationConsensusStore?: AnnotationStoreT; }' is not assignable to type...
  frames[frameIndex] = {
    ...frames[frameIndex],
    annotationStore: updatedAnnotationStore
  };
  const updatedAnnotationIds = [...state.selectedAnnotationIds];
  const idx = updatedAnnotationIds.indexOf(annotationIdx);
  // Reset index if selected annotation was deleted
  if (idx >= 0) {
    updatedAnnotationIds.splice(idx, 1);
  }
  atomSetter?.setAnnotationFrames(frames);
  atomSetter?.setSelectedAnnotationIds(updatedAnnotationIds);
  return {
    ...state,
    frames,
    annotationReviewStore: updatedAnnotationReviewStore,
    // Reset index if selected annotation was deleted
    selectedAnnotationIds: updatedAnnotationIds
  };
};
export const deleteAnnotations = (state: LabelStateT, deletedAnnotationStores: AnnotationStoreT[], deletedAnnotationsReviewData?: GenericAnnotationReviewDataT[], atomSetter?: AtomSetter) => {
  const frames: FrameStateT[] = [...state.frames];
  const updatedFrames = frames.map((frame, index) => {
    // @ts-expect-error todo(ts-migration) TS2769 No overload matches this call.
    const currentFrameDeletedAnnotations = Object.keys(deletedAnnotationStores[index]);
    const updatedAnnotationStore = Object.entries(frame.annotationStore).filter(([annotationId, _]) => !currentFrameDeletedAnnotations.includes(annotationId));
    return {
      ...frame,
      annotationStore: Object.fromEntries(updatedAnnotationStore)
    };
  });
  const updatedAnnotationReviewStore = Object.entries(state.annotationReviewStore).filter(([_, annotation]) => {
    return !deletedAnnotationsReviewData?.includes(annotation);
  });
  atomSetter?.setAnnotationFrames(updatedFrames);
  return {
    ...state,
    frames: updatedFrames,
    annotationReviewStore: Object.fromEntries(updatedAnnotationReviewStore)
  };
};
export const importAnnotations = (state: LabelStateT, payload: ImportAnnotationsPayloadT, existingAddedAnnotationIds?: string[], atomSetter?: AtomSetter) => {
  const addedAnnotationIds: string[] = [];
  const updatedAnnotations: {
    [id: string]: AnnotationT;
  } = {};
  const frameIndex = payload.frameIndex;
  const updatedState = payload.annotationIdList.reduce<LabelStateT>((currentState, annotationId, index) => {
    // Check ML Store first
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    let annotationData = currentState.frames[frameIndex].annotationMLStore?.[annotationId];
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    let sourceStore = currentState.frames[frameIndex].annotationMLStore;
    let isFromConsensusStore = false;

    // If not found in ML store, check consensus store
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    if (!annotationData && currentState.frames[frameIndex].annotationConsensusStore) {
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      annotationData = currentState.frames[frameIndex].annotationConsensusStore[annotationId];
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      sourceStore = currentState.frames[frameIndex].annotationConsensusStore;
      isFromConsensusStore = true;
    }
    if (!annotationData || !sourceStore) {
      return currentState;
    }

    // Hide the annotation in the source store
    if (isFromConsensusStore) {
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      currentState.frames[frameIndex].annotationConsensusStore = {
        ...showHideAnnotationFromAnnotationStore(annotationId, true, sourceStore)
      };
    } else {
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      currentState.frames[frameIndex].annotationMLStore = {
        ...showHideAnnotationFromAnnotationStore(annotationId, true, sourceStore)
      };
    }
    /**
     * TODO: remove this check and handle properly for SEGMENTATION annotation
     * In segmentation we support one annotation only for each label, so checking
     * if initially existed and updating (add data points) in that annotation.
     */
    if (annotationData.annotationType === ANNOTATION_TYPE.SEGMENTATION) {
      const alreadyExistingAnnotationId = getSegmentationAnnotationId(
      // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'FrameStateT | undefined' is not assignable to parameter of type 'FrameStateT'.
      currentState.frames[frameIndex], annotationData.annotationLabel);
      if (alreadyExistingAnnotationId) {
        // @ts-expect-error todo(ts-migration) TS2322 Type 'AnnotationT | undefined' is not assignable to type 'AnnotationT'.
        updatedAnnotations[alreadyExistingAnnotationId] =
        // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
        currentState.frames[frameIndex].annotationStore[alreadyExistingAnnotationId];
        return handleImportSegmentationAnnotation(currentState, payload, annotationData, alreadyExistingAnnotationId, atomSetter);
      }
    }
    const annotationIdToAdd = existingAddedAnnotationIds?.[index] || getRandomId();
    addedAnnotationIds.push(annotationIdToAdd);
    const annotationDataWithFixedBound = boundCoordinate(annotationData, state.metaData);
    return addAnnotation(currentState, {
      frameIndex,
      annotationIdx: annotationIdToAdd,
      annotationData: {
        ...annotationDataWithFixedBound,
        hidden: false,
        metadata: {
          ...annotationDataWithFixedBound.metadata,
          // Only add mlData if it's from ML store, preserve consensus metadata if from consensus store
          ...(isFromConsensusStore ? {} : {
            mlData: {
              ...(annotationDataWithFixedBound.metadata?.mlData || {}),
              state: ANNOTATION_ML_STATE.AUTOMATED
            }
          })
        }
      },
      ...(payload.reviewAction && {
        annotationReviewData: initializeAnnotationReviewData({
          ...annotationDataWithFixedBound,
          id: annotationIdToAdd
        }, payload.reviewAction)
      })
    }, {
      shouldSetSelectedAnnotationIds: false,
      shouldShowAnnotationDetails: false
    }, atomSetter);
  }, state);
  return {
    updatedState,
    addedAnnotationIds,
    updatedAnnotations
  };
};
const getSegmentationAnnotationId = (frame: FrameStateT, annotationLabel: AnnotationLabelT): string | undefined | null => {
  return Object.keys(frame.annotationStore).find(annotationId => {
    const annotation = frame.annotationStore[annotationId];
    return (
      // @ts-expect-error todo(ts-migration) TS18048 'annotation' is possibly 'undefined'.
      annotation.annotationType === ANNOTATION_TYPE.SEGMENTATION &&
      // @ts-expect-error todo(ts-migration) TS18048 'annotation' is possibly 'undefined'.
      annotation.annotationLabel.title === annotationLabel.title
    );
  });
};
const getUpdatedSegmentationAnnotationData = (annotation: BrushAnnotationT, points: [number, number][]): BrushAnnotationT => {
  return {
    ...annotation,
    data: {
      ...annotation.data,
      points: [...(annotation.data.points ?? []), ...points]
    }
  };
};
const handleImportSegmentationAnnotation = (state: LabelStateT, payload: ImportAnnotationsPayloadT, annotationData: BrushAnnotationT, annotationId: string, atomSetter?: AtomSetter): LabelStateT => {
  const frameIndex = payload.frameIndex;
  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  const originalAnnotation = state.frames[frameIndex].annotationStore[annotationId];

  // @ts-expect-error todo(ts-migration) TS18048 'originalAnnotation' is possibly 'undefined'.
  if (originalAnnotation.annotationType !== ANNOTATION_TYPE.SEGMENTATION) return state;
  const updatedAnnotationData = getUpdatedSegmentationAnnotationData(
  // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'AnnotationT | undefined' is not assignable to parameter of type 'BrushAnnotationT'.
  originalAnnotation, annotationData.data.points ?? []);

  // Set annotation ML state as assisted as there is an existing annotation
  updatedAnnotationData.metadata = {
    ...updatedAnnotationData.metadata,
    mlData: {
      ...(updatedAnnotationData.metadata?.mlData || {}),
      state: ANNOTATION_ML_STATE.ASSISTED
    }
  };
  const annotationReviewData = initializeAnnotationReviewData({
    ...annotationData,
    id: annotationId
  }, payload.reviewAction || REVIEW_ACTION_TYPE.PENDING);
  return updateAnnotation(state, {
    frameIndex,
    annotationIdx: annotationId,
    annotationData: updatedAnnotationData,
    ...(payload.reviewAction && {
      annotationReviewData
    })
  }, atomSetter);
};
export const updateAnnotationMLData = (state: LabelStateT, payload: Omit<UpdateAnnotationActionT, 'type'>, atomSetter?: AtomSetter) => {
  const {
    annotationData
  } = payload;

  // If the annotation is not an ML annotation, return the state as is
  if (!annotationData.metadata?.mlData) {
    return state;
  }
  const updatedAnnotationMLData = {
    ...annotationData.metadata.mlData
  };
  // If the annotation is automated, change it to assisted on update
  if (updatedAnnotationMLData.state === ANNOTATION_ML_STATE.AUTOMATED) {
    updatedAnnotationMLData.state = ANNOTATION_ML_STATE.ASSISTED;
  }
  const updatedAnnotation = {
    ...annotationData,
    metadata: {
      ...annotationData.metadata,
      mlData: updatedAnnotationMLData
    }
  };
  return updateAnnotation(state, {
    ...payload,
    annotationData: updatedAnnotation
  }, atomSetter);
};
export const deleteAnnotationsInCurrentFrame = (state: LabelStateT, payload: DeleteAnnotationsPayloadT) => {
  let currentFrameDeletedAnnotations = ({} as AnnotationStoreT);
  let frames = [...state.frames];

  // delete all annotations in current frame
  if (payload.currentFrame !== undefined && !payload.annotationLabels?.length) {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    currentFrameDeletedAnnotations = {
      ...frames[payload.currentFrame].annotationStore
    };
    // @ts-expect-error todo(ts-migration) TS2322 Type '{ annotationStore: {}; imageUrl?: string | undefined; annotationMLStore?: AnnotationStoreT; annotationConsensusStore?: AnnotationStoreT; }' is not assignable to type 'FrameStateT'.
    frames[payload.currentFrame] = {
      ...frames[payload.currentFrame],
      annotationStore: {}
    };
  } else {
    // delete label specific annotations in current frame
    frames = frames.map((frame, index) => {
      if (index === payload.currentFrame) {
        const [updatedAnnotationStore, deletedAnnotations] = Object.entries(frame.annotationStore).reduce<[AnnotationStoreT, AnnotationStoreT]>(([updatedStore, deletedAnnotations], [annotationId, annotation]) => {
          if (!(payload?.annotationLabels ?? []).includes(annotation.annotationLabel.title)) {
            return [{
              ...updatedStore,
              [annotationId]: annotation
            }, deletedAnnotations];
          }
          return [updatedStore, ({
            ...deletedAnnotations,
            [annotationId]: annotation
          } as AnnotationStoreT)];
        }, [{}, {}]);
        currentFrameDeletedAnnotations = deletedAnnotations;
        return {
          ...frame,
          annotationStore: updatedAnnotationStore
        };
      }
      return frame;
    });
  }
  const [updatedAnnotationReviewStore, deletedReviewData] = Object.entries(state.annotationReviewStore).reduce<[AnnotationReviewStoreT, AnnotationReviewStoreT]>(([updatedStore, deletedData], [annotationId, annotation]) => {
    if (currentFrameDeletedAnnotations[annotationId]) {
      return [updatedStore, ({
        ...deletedData,
        [annotationId]: annotation
      } as AnnotationReviewStoreT)];
    }
    return [({
      ...updatedStore,
      [annotationId]: annotation
    } as AnnotationReviewStoreT), deletedData];
  }, [{}, {}]);
  return {
    frames,
    currentFrameDeletedAnnotations,
    updatedAnnotationReviewStore,
    deletedReviewData
  };
};
export const getParentAnnotations = (annotationStore: AnnotationStoreT, deletedAnnotations: AnnotationStoreT) => {
  return Object.entries(annotationStore).reduce((acc, [annotationId, annotation]: [string, AnnotationT]) => {
    const childId = annotation.children?.find((id: string) => Object.keys(deletedAnnotations).includes(id));
    return childId ? {
      ...acc,
      [annotationId]: annotation
    } : acc;
  }, {});
};
export const removeDeletedChildrenFromParent = (frames: FrameStateT[], parentAnnotations: AnnotationStoreT | undefined, deletedAnnotations: AnnotationStoreT | undefined, currentFrame: number): FrameStateT => {
  const updatedParentAnnotations = parentAnnotations && Object.entries(parentAnnotations).reduce<{
    [key: string]: AnnotationT;
  }>((updatedAnnotationStore, [parentId, parentAnnotation]) => {
    const updatedParentAnnotation = {
      ...parentAnnotation,
      ...(parentAnnotation.children && {
        children: parentAnnotation.children.filter(id => deletedAnnotations && !Object.keys(deletedAnnotations).includes(id))
      })
    };
    return {
      ...updatedAnnotationStore,
      [parentId]: updatedParentAnnotation
    };
  }, {});
  const currentFrameInfo = frames[currentFrame];
  return {
    ...currentFrameInfo,
    imageUrl: currentFrameInfo?.imageUrl ?? '',
    annotationStore: {
      ...currentFrameInfo?.annotationStore,
      ...updatedParentAnnotations
    }
  };
};
export const removeDeletedChildFromParent = (state: LabelStateT, payload: DeleteAnnotationPayloadT, parentAnnotation?: {
  id: string;
  annotation: AnnotationT;
}, atomSetter?: AtomSetter) => {
  if (!parentAnnotation) return state;

  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  let currentAnnotationStore = state.frames[payload.frameIndex].annotationStore;
  if (parentAnnotation) {
    currentAnnotationStore = {
      ...currentAnnotationStore,
      [parentAnnotation.id]: {
        ...parentAnnotation.annotation,
        children: parentAnnotation.annotation?.children?.filter((id: string) => id !== payload.annotationIdx)
      }
    };
  }
  const updatedFrames: FrameStateT[] = [...state.frames.slice(0, payload.frameIndex), ({
    ...state.frames[payload.frameIndex],
    annotationStore: currentAnnotationStore
  } as FrameStateT), ...state.frames.slice(payload.frameIndex + 1)];
  atomSetter?.setAnnotationFrames(updatedFrames);
  return {
    ...state,
    frames: updatedFrames
  };
};